import React from "react";
import { cn } from "../../lib/utils";
import HelpLink from "../help/HelpLink";

const DOT = {
  resistance: "bg-red-400",
  anaerobic: "bg-amber-400",
  aerobic: "bg-sky-400",
};

const formatMinutes = (min) => {
  if (!min || min < 1) return "0 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
};

const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const TARGET_SOURCE = {
  programme: "from your programme",
  profile: "from your profile",
  history: "from your last 8 weeks",
};

/**
 * The last 7 days at a glance: days trained against the days a week you aim
 * for, the weeks-on-target streak, the weekly targets What's next plans from,
 * and hard sets per major muscle. One rolling window for all of it, so the
 * numbers agree (the streak counts whole Monday-to-Sunday weeks).
 */
const WeekOverview = ({
  summary,
  target,
  weeksOnTarget,
  daysTrained,
  onChangeTarget,
  suggestion,
}) => {
  if (!summary) return null;
  const maxSets = summary.setCeiling || summary.setTarget * 2;

  return (
    <section
      aria-label="Last 7 days"
      className="grid gap-3 rounded-2xl border border-border bg-card p-3.5"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1 text-[15px] font-bold text-white">
          Last 7 days
          <HelpLink topic="weekly-targets" label="About weekly targets" />
        </h3>
        <span className="text-xs text-muted-foreground tabular-nums">
          {summary.count} {summary.count === 1 ? "session" : "sessions"} ·{" "}
          {formatMinutes(summary.minutes)}
        </span>
      </div>

      {/* The headline: days trained against the target, and the streak. */}
      <div className="grid gap-0.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <p className="text-muted-foreground">
            <span className="text-[22px] font-bold tabular-nums text-white">
              {daysTrained} of {target.days}
            </span>{" "}
            <span className="text-xs">days</span>
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border py-0.5 pl-2.5 pr-1 text-xs text-foreground">
            <span
              aria-hidden
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                weeksOnTarget > 0 ? "bg-secondary" : "bg-white/20",
              )}
            />
            <span className="tabular-nums">
              {weeksOnTarget} {weeksOnTarget === 1 ? "week" : "weeks"} on target
            </span>
            <HelpLink
              topic="activity#weeks-on-target"
              label="About weeks on target"
              className="h-5 w-5"
            />
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Target {target.days} a week, {TARGET_SOURCE[target.source] || "assumed for now"}
          {onChangeTarget && (
            <>
              {" · "}
              <button
                type="button"
                onClick={onChangeTarget}
                className="font-medium text-primary hover:underline"
              >
                Change
              </button>
            </>
          )}
        </p>
      </div>

      <div className="grid gap-2">
        {summary.rows.map((row) => {
          // Not a target this week (e.g. intervals for a muscle goal): show
          // what was done as a bonus rather than "1 of 0".
          if (row.target === 0) {
            return (
              <div
                key={row.tag}
                className="grid grid-cols-[0.5625rem_minmax(0,1fr)_auto] items-center gap-2.5"
              >
                <span className={cn("h-2.5 w-2.5 rounded-full opacity-40", DOT[row.tag])} />
                <span className="text-sm text-muted-foreground">{row.label}</span>
                <span className="text-xs text-muted-foreground">
                  {row.done > 0 ? `${row.done} done · bonus` : "not a target"}
                </span>
              </div>
            );
          }
          const met = row.done >= row.target;
          return (
            <div
              key={row.tag}
              className="grid grid-cols-[0.5625rem_minmax(0,1fr)_auto] items-center gap-2.5"
            >
              <span className={cn("h-2.5 w-2.5 rounded-full", DOT[row.tag])} />
              <span className="text-sm text-foreground">{row.label}</span>
              <span className="flex items-center gap-1">
                {Array.from({ length: Math.max(row.target, row.done) }, (_, i) => (
                  <span
                    key={i}
                    className={cn(
                      "h-1.5 w-[18px] rounded-full",
                      i < row.done ? DOT[row.tag] : "bg-white/10",
                    )}
                  />
                ))}
                <span
                  className={cn(
                    "ml-1.5 min-w-[2.6rem] text-right text-xs tabular-nums",
                    met ? "text-secondary" : "text-muted-foreground",
                  )}
                >
                  {row.done} of {row.target}
                </span>
              </span>
            </div>
          );
        })}
        {summary.zone2 && (
          // Real Zone 2 time (#94), from sessions that recorded heart rate.
          <div className="grid grid-cols-[0.5625rem_minmax(0,1fr)_auto] items-center gap-2.5">
            <span />
            <span className="text-xs text-muted-foreground">
              Zone 2 by heart rate
              <span className="text-muted-foreground/70">
                {" "}
                · {summary.zone2.sessions} {summary.zone2.sessions === 1 ? "session" : "sessions"}
              </span>
            </span>
            <span
              className={cn(
                "text-xs tabular-nums",
                !summary.zone2.target
                  ? "text-muted-foreground"
                  : summary.zone2.minutes >= summary.zone2.target
                    ? "text-secondary"
                    : "text-amber-400",
              )}
            >
              {summary.zone2.target
                ? `${summary.zone2.minutes} of ${summary.zone2.target} min`
                : `${summary.zone2.minutes} min`}
            </span>
          </div>
        )}
      </div>
      {summary.note && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {summary.note}
        </p>
      )}

      {summary.muscles && (
        <div className="grid gap-1.5 border-t border-border pt-3">
          <p className="mb-0.5 flex items-center justify-between text-xs font-semibold text-muted-foreground">
            <span className="flex items-center gap-1">
              Hard sets per muscle
              <HelpLink topic="weekly-targets#hard-sets-per-muscle" label="What's a hard set?" />
            </span>
            <span className="font-normal text-muted-foreground/70">
              aim for {summary.setTarget}–{maxSets}
            </span>
          </p>
          {/* Lowest first, so what's short reads first. The shaded band is the
              10-20 range; short bars are grey, met ones red. */}
          {[...summary.muscles]
            .sort((a, b) => a.sets - b.sets)
            .map(({ muscle, sets }) => (
              <div
                key={muscle}
                className="grid grid-cols-[5.25rem_minmax(0,1fr)_1.75rem] items-center gap-2 text-xs"
              >
                <span className="text-muted-foreground">{capitalise(muscle)}</span>
                <span className="relative h-2 overflow-hidden rounded-full bg-white/[0.05]">
                  <span
                    aria-hidden
                    className="absolute inset-y-0 right-0 border-l border-secondary/50 bg-secondary/[0.08]"
                    style={{ left: `${(summary.setTarget / maxSets) * 100}%` }}
                  />
                  <span
                    className={cn(
                      "absolute inset-y-0 left-0 rounded-full",
                      sets >= summary.setTarget ? "bg-red-400" : "bg-foreground/35",
                    )}
                    style={{ width: `${(Math.min(sets, maxSets) / maxSets) * 100}%` }}
                  />
                </span>
                <span className="text-right font-semibold tabular-nums text-foreground">
                  {sets}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Suggested, never imposed: the user decides whether to raise it. */}
      {suggestion && (
        <div className="grid gap-2 rounded-xl border border-primary/30 bg-primary/[0.07] p-3">
          <p className="text-sm text-foreground">
            From 65, WHO advises strength and balance work on 3 or more days a
            week. Raise your strength target to 3?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={suggestion.onAccept}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Raise to 3
            </button>
            <button
              type="button"
              onClick={suggestion.onDismiss}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default WeekOverview;
